import React, { useEffect, useRef, useState } from 'react';
import { Play, Volume2, VolumeX, ChevronLeft, ChevronRight, Calendar, Heart, Camera, Video } from 'lucide-react';
import { Memory, getImageStyleFromUrl } from '../types';

interface MemoryCardProps {
  key?: React.Key;
  memory: Memory;
  onSelect?: (memory: Memory) => void;
}

export default function MemoryCard({ memory, onSelect }: MemoryCardProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [likes, setLikes] = useState(memory.likes || 0);
  const [hasLiked, setHasLiked] = useState(false);

  // Helper to safely parse media URLs list
  const getMediaUrls = (): string[] => {
    try {
      if (memory.mediaUrl && memory.mediaUrl.trim().startsWith('[')) {
        const parsed = JSON.parse(memory.mediaUrl);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {
      console.error('Failed to parse mediaUrl JSON:', e);
    }
    return [memory.mediaUrl];
  };

  const mediaUrls = getMediaUrls();

  // Reset image index when memory changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [memory.id]);

  // Intersection Observer to pause video when it scrolls out of viewport
  useEffect(() => {
    if (memory.mediaType !== 'video' || !videoRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (videoRef.current) {
            if (!entry.isIntersecting) {
              videoRef.current.pause();
              setIsPlaying(false);
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [memory.mediaType]);

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (memory.mediaType !== 'video' || !videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasLiked) {
      setLikes(prev => prev + 1);
      setHasLiked(true);
      fetch(`/api/memories/${memory.id}/like`, { method: 'POST' }).catch(() => {});
    }
  };

  const formattedDate = () => {
    if (!memory.date) return '';
    try {
      const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
      const dateObj = new Date(memory.date);
      return dateObj.toLocaleDateString('en-US', options);
    } catch {
      return memory.date;
    }
  };

  return (
    <article 
      ref={containerRef}
      className="card-estate w-full max-w-xl mx-auto overflow-hidden bg-white border border-[#E6E1D4] shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group cursor-pointer"
      onClick={() => onSelect && onSelect(memory)}
    >
      {/* Top Card Bar: Media Type Badge, Date, and Like Button */}
      <div className="p-4 sm:p-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="estate-chip text-[10px] py-1 px-2.5">
            {memory.mediaType === 'video' ? (
              <>
                <Video size={11} className="text-emerald-700" /> Reel
              </>
            ) : (
              <>
                <Camera size={11} className="text-emerald-700" /> Story
              </>
            )}
          </span>
          {memory.date && (
            <span className="flex items-center gap-1 text-xs text-[#65756C] font-medium">
              <Calendar size={12} className="text-emerald-800" />
              {formattedDate()}
            </span>
          )}
        </div>

        <button
          onClick={handleLike}
          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer ${
            hasLiked 
              ? 'bg-rose-50 text-rose-600 border border-rose-200' 
              : 'text-[#65756C] hover:text-rose-600 hover:bg-rose-50 border border-[#E6E1D4]'
          }`}
          title="Save to favorites"
        >
          <Heart size={13} className={hasLiked ? 'fill-rose-500 text-rose-500' : ''} />
          <span>{likes}</span>
        </button>
      </div>

      {/* Media Content Area */}
      <div 
        className="relative w-full aspect-[4/3] bg-stone-900 overflow-hidden select-none"
        onClick={memory.mediaType === 'video' ? togglePlay : undefined}
      >
        {memory.mediaType === 'video' ? (
          <div className="relative w-full h-full">
            <video
              ref={videoRef}
              src={mediaUrls[0]}
              className="w-full h-full object-cover"
              loop
              muted={isMuted}
              playsInline
              preload="metadata"
            />
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/25 pointer-events-none transition-all duration-300">
                <div className="bg-white/30 backdrop-blur-md p-4 rounded-full border border-white/30 text-white shadow-lg">
                  <Play size={28} fill="white" className="translate-x-0.5" />
                </div>
              </div>
            )}
            <button
              onClick={toggleMute}
              className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-md hover:bg-black/80 text-white rounded-full p-2 transition-colors z-10 cursor-pointer border border-white/10"
            >
              {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
          </div>
        ) : (
          <div className="relative w-full h-full">
            <img
              src={mediaUrls[currentImageIndex]}
              alt={`${memory.title} - ${currentImageIndex + 1}`}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-700 ease-out select-none"
              style={getImageStyleFromUrl(mediaUrls[currentImageIndex])}
              key={currentImageIndex}
            />

            {mediaUrls.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(prev => (prev === 0 ? mediaUrls.length - 1 : prev - 1));
                  }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/70 text-white rounded-full p-2 transition-colors cursor-pointer border border-white/20 z-10"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(prev => (prev === mediaUrls.length - 1 ? 0 : prev + 1));
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/70 text-white rounded-full p-2 transition-colors cursor-pointer border border-white/20 z-10"
                >
                  <ChevronRight size={16} />
                </button>

                <div className="absolute bottom-3 inset-x-0 flex justify-center z-10">
                  <div className="flex space-x-1.5 bg-black/40 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/10">
                    {mediaUrls.map((_, idx) => (
                      <span
                        key={idx}
                        className={`w-1.5 h-1.5 rounded-full transition-all ${
                          idx === currentImageIndex ? 'bg-white scale-125' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                <span className="absolute top-3 right-3 bg-black/60 backdrop-blur-md text-white text-[10px] font-semibold px-2.5 py-0.5 rounded-full z-10 border border-white/10">
                  {currentImageIndex + 1}/{mediaUrls.length}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Narrative & Info Block */}
      <div className="p-5 flex-grow flex flex-col justify-between">
        <div>
          <h3 className="font-serif text-xl sm:text-2xl font-semibold text-[#1A241F] leading-snug group-hover:text-[#14532D] transition-colors mb-2">
            {memory.title}
          </h3>

          {memory.description && (
            <p className="font-sans text-sm text-[#37463E] leading-relaxed line-clamp-3">
              "{memory.description}"
            </p>
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-[#E6E1D4]/60 flex items-center justify-between text-xs text-[#65756C]">
          <span>Mithran Farm House</span>
          <span className="text-emerald-800 font-semibold group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-0.5">
            View full story →
          </span>
        </div>
      </div>
    </article>
  );
}
